import React from 'react';
import { AccommodationCombined } from '../../Accommodations/Accommodation.types';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useTranslation } from '@shared/core';
import { Box, BoxProps, Divider, Flex, JoyKitThemeProvider, TextV2, TooltipV2 } from '@withjoy/joykit';
import { Skeleton, SkeletonGroup } from '@shared/components/Skeleton';
import { TravelCurrencyCode } from '@graphql/generated';
import { useQueryParamHelper } from '@shared/core/queryString';
import { useTravelPlacesPricingAndBookingDetailsFragment } from '@shared/hooks/useTravelPlacesPricingAndBookingDetails';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { useTravelWhiteLabelCurrency } from '@shared/hooks/useTravelWhiteLabelCurrency';
import { Help, Info } from '@withjoy/joykit/icons';
import { ExpediaIcon } from '@assets/icons';
import { pxToRem } from '@withjoy/joykit/theme';
import { PercentageOffBadge } from './PercentageOffBadge';
import { SoldOutTag } from '@shared/components/SoldOutTag/SoldOutTag';

type JoyPlacePricingProps = { accommodation: AccommodationCombined; isGridView?: boolean; isFromTiles?: boolean; isLargeTile?: boolean; showEventOnlyRateTag?: boolean } & BoxProps;

export const JoyPlacePricing = ({ accommodation, isGridView, isFromTiles = false, isLargeTile = true, showEventOnlyRateTag = true, ...boxProps }: JoyPlacePricingProps) => {
  const { eventInfo } = useEventInfo();
  const eventDisplayName = eventInfo?.eventDisplayName;
  const { t: accommodationsTrans } = useTranslation('guestSiteCustom');
  const accTrans = accommodationsTrans('accommodations');
  const [accommodationGuestHotelTileFontUpdatesEnabled] = useIsFeatureEnabled('accommodationGuestHotelTileFontUpdates');

  const { price, strikeOutPrice, percentageOff, loading, soldOutUrl } = useTravelPlacesPricingAndBookingDetailsFragment(accommodation.id, 'accommodationPlace');

  if (soldOutUrl) {
    return (
      <Box {...boxProps}>
        <SoldOutTag />
      </Box>
    );
  }

  return (
    ((price || loading) && (
      <JoyKitThemeProvider>
        <Box {...boxProps}>
          {/* Conditionally render Divider for legacy UI */}
          {!isFromTiles && <Divider marginBottom={5} />}

          <SkeletonGroup
            isReady={!loading}
            placeholder={
              isFromTiles ? (
                // Modern UI skeleton for tiles
                <Flex
                  justifyContent={isGridView ? 'flex-end' : 'space-between'}
                  {...(!isGridView ? { borderTop: '1px solid', borderTopColor: 'mono3', paddingTop: 4 } : {})}
                >
                  {!isGridView && (
                    <Skeleton
                      placeholder={
                        <Box
                          width={'7rem'}
                          height={pxToRem(16)}
                          backgroundColor={'mono3'}
                        >
                          &nbsp;
                        </Box>
                      }
                    />
                  )}
                  <Flex
                    flexDirection="column"
                    gap={1}
                    alignItems="flex-end"
                  >
                    {/* Event only Rate label */}
                    <Skeleton
                      placeholder={
                        <Box
                          width={'5rem'}
                          height={pxToRem(14)}
                          backgroundColor={'mono3'}
                        >
                          &nbsp;
                        </Box>
                      }
                    />
                    {/* Price */}
                    <Skeleton
                      placeholder={
                        <Box
                          width={'4rem'}
                          height={pxToRem(18)}
                          backgroundColor={'mono3'}
                        >
                          &nbsp;
                        </Box>
                      }
                    />
                  </Flex>
                </Flex>
              ) : (
                // Legacy UI skeleton
                <Flex
                  flexDirection="column"
                  gap={3}
                >
                  {/* Exclusive Rate label */}
                  <Flex justifyContent="flex-end">
                    <Skeleton
                      placeholder={
                        <Box
                          width={'5rem'}
                          height={pxToRem(14)}
                          backgroundColor={'mono3'}
                        >
                          &nbsp;
                        </Box>
                      }
                    />
                  </Flex>
                  {/* Price row */}
                  <Flex
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Skeleton
                      placeholder={
                        <Box
                          width={'7rem'}
                          height={pxToRem(16)}
                          backgroundColor={'mono3'}
                        >
                          &nbsp;
                        </Box>
                      }
                    />
                    <Skeleton
                      placeholder={
                        <Box
                          width={'4rem'}
                          height={pxToRem(20)}
                          backgroundColor={'mono3'}
                        >
                          &nbsp;
                        </Box>
                      }
                    />
                  </Flex>
                </Flex>
              )
            }
          >
            {price && (
              <>
                {/* Modern UI Layout */}
                {isFromTiles ? (
                  <Flex
                    {...(accommodationGuestHotelTileFontUpdatesEnabled && { alignItems: showEventOnlyRateTag || percentageOff || strikeOutPrice ? 'flex-start' : 'center' })}
                    justifyContent={isGridView && isLargeTile ? 'flex-end' : 'space-between'}
                    {...(!isGridView || !isLargeTile ? { borderTop: '1px solid', borderTopColor: 'mono3', paddingTop: 4 } : {})}
                  >
                    {(!isGridView || !isLargeTile) && (
                      <TextV2
                        typographyVariant="body1"
                        color="mono8"
                        fontWeight={400}
                      >
                        {accTrans.roomsStartingFrom()}
                      </TextV2>
                    )}
                    <Box color={'black'}>
                      <Flex
                        alignItems={'center'}
                        gap={3}
                        marginBottom={1}
                      >
                        {showEventOnlyRateTag && (
                          <Flex
                            alignItems={'center'}
                            justifyContent={'flex-end'}
                            gap={1}
                          >
                            <TextV2
                              typographyVariant={'body1'}
                              color={'accentGreen3'}
                              fontWeight={400}
                              letterSpacing="-0.15px"
                            >
                              {accommodationsTrans('accommodations', 'eventOnlyRate')()}
                            </TextV2>
                            {eventDisplayName ? (
                              <TooltipV2
                                content={accTrans.eventOnlyRateInfo({ name: eventDisplayName })}
                                onMouseEnterDelay={500}
                                placement={'topEnd'}
                                overrides={{
                                  Content: { props: { backgroundColor: 'white', color: 'mono14', maxWidth: '256px', typographyVariant: 'label2' } },
                                  Arrow: { props: { backgroundColor: 'white' } },
                                }}
                              >
                                <Help
                                  size={'sm'}
                                  color={'accentGreen3'}
                                  title={undefined}
                                />
                              </TooltipV2>
                            ) : null}
                          </Flex>
                        )}
                        {percentageOff && isLargeTile && <PercentageOffBadge percentage={percentageOff} />}
                      </Flex>
                      <Flex
                        alignItems={'center'}
                        gap={2}
                        justifyContent={'flex-end'}
                      >
                        {strikeOutPrice && isLargeTile && (
                          <StrikeOutPrice
                            strikeOutPrice={strikeOutPrice}
                            isFromTiles={isFromTiles}
                          />
                        )}
                        <Box
                          display={'flex'}
                          typographyVariant={isLargeTile ? 'hed4' : 'hed2'}
                          alignItems={'center'}
                          justifyContent={'flex-end'}
                          letterSpacing="-0.27px"
                        >
                          <IsoCurrencyIndicator />
                          {accTrans.pricePerNight({ price })}
                        </Box>
                      </Flex>
                      {!isLargeTile && (
                        <Flex
                          alignItems={'center'}
                          gap={2}
                          justifyContent={'flex-end'}
                        >
                          {strikeOutPrice && (
                            <StrikeOutPrice
                              strikeOutPrice={strikeOutPrice}
                              isFromTiles={isFromTiles}
                            />
                          )}
                          {percentageOff && <PercentageOffBadge percentage={percentageOff} />}
                        </Flex>
                      )}
                    </Box>
                  </Flex>
                ) : (
                  /* Legacy UI Layout */
                  <>
                    <Box color={'black'}>
                      <Box
                        display={'flex'}
                        typographyVariant={'body1'}
                        alignItems={'center'}
                        justifyContent={'flex-end'}
                        marginBottom={1}
                      >
                        {accommodationsTrans('accommodations', 'exclusiveRate')()}
                        {eventDisplayName ? (
                          <Box marginLeft={2}>
                            <TooltipV2
                              content={accTrans.eventOnlyRateInfo({ name: eventDisplayName })}
                              onMouseEnterDelay={500}
                              placement={'topEnd'}
                            >
                              <Info
                                size={'sm'}
                                color={'mono10'}
                                title={undefined}
                              />
                            </TooltipV2>
                          </Box>
                        ) : null}
                      </Box>
                      <Box
                        display={'flex'}
                        alignItems={'center'}
                        justifyContent={'space-between'}
                        marginTop={3}
                      >
                        <Box
                          typographyVariant={'body1'}
                          color={'mono14'}
                          marginRight={2}
                        >
                          {accTrans.roomsStartingFrom()}
                        </Box>
                        <Box
                          display={'flex'}
                          alignItems={'center'}
                          justifyContent={'flex-end'}
                        >
                          <Box
                            typographyVariant={'hed2'}
                            justifyContent={'flex-end'}
                            display={'flex'}
                          >
                            <IsoCurrencyIndicator />
                            {accTrans.pricePerNight({ price })}
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                    {strikeOutPrice ? (
                      <StrikeOutPrice
                        strikeOutPrice={strikeOutPrice}
                        isFromTiles={isFromTiles}
                      />
                    ) : null}
                  </>
                )}
              </>
            )}
          </SkeletonGroup>
        </Box>
      </JoyKitThemeProvider>
    )) ||
    null
  );
};

export const IsoCurrencyIndicator = () => {
  const currency = useTravelWhiteLabelCurrency();
  const [localeBasedCurrencyEnabled] = useIsFeatureEnabled('localeBasedHotelCurrency');
  const queryParamHelper = useQueryParamHelper();
  const showCurrencyCode = queryParamHelper.getValueString('showCurrencyCode') === 'true';

  return showCurrencyCode && currency ? <Box marginRight={2}>{localeBasedCurrencyEnabled ? currency : TravelCurrencyCode.USD}</Box> : null;
};

const StrikeOutPrice = ({ strikeOutPrice, isFromTiles }: { strikeOutPrice: string; isFromTiles: boolean }) => {
  const { t: websiteEditorTrans } = useTranslation('websiteEditor');
  const [accommodationGuestHotelTileFontUpdatesEnabled] = useIsFeatureEnabled('accommodationGuestHotelTileFontUpdates');

  const expediaCompareTo = websiteEditorTrans('expediaCompareTo')({
    ExpediaCompareTo: <ExpediaIcon />,
  });

  return isFromTiles ? (
    <TextV2
      typographyVariant={accommodationGuestHotelTileFontUpdatesEnabled ? 'body3' : 'body1'}
      {...(!accommodationGuestHotelTileFontUpdatesEnabled && { fontSize: pxToRem(16) })}
      textDecoration={'line-through'}
      color={'mono8'}
    >
      {strikeOutPrice}
    </TextV2>
  ) : (
    <Box
      typographyVariant={'body3'}
      height={pxToRem(48)}
      alignItems={'center'}
      color={'inputPlaceholderDisabled'}
      display={'flex'}
      borderTop={'solid 1px'}
      borderColor={'mono5'}
      justifyContent={'space-between'}
      marginTop={3}
    >
      <Box
        typographyVariant="body1"
        color={accommodationGuestHotelTileFontUpdatesEnabled ? '#191E3B' : 'mono8'}
        {...(!accommodationGuestHotelTileFontUpdatesEnabled && { fontSize: pxToRem(16) })}
        alignItems="center"
        gap={1}
        marginRight={2}
      >
        {expediaCompareTo}
      </Box>
      <Box
        textDecoration={'line-through'}
        color={'mono14'}
      >
        {strikeOutPrice}
      </Box>
    </Box>
  );
};
